import type { History } from "history";
import { startTransition, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

// Changing page keeps the old page on screen until the new one has rendered,
// then the old page leaves and the new one rises into place (styles/index.css),
// instead of blanking the content and showing a spinner while a page's code
// loads. Only the path counts: a changed query string is a filter or a table
// page, which must update at once.

// Past this the crossfade stops waiting, so the screen isn't frozen for long.
const MAX_HOLD_MS = 300;
// A little longer if the page has rendered but for a spinner. The old page is
// gone by then, and React keeps a spinner it just showed for 300ms, however soon
// the code arrives.
const MAX_HOLD_FOR_SPINNER_MS = 500;

interface Hold {
  key: string;
  update: () => void;
  started: boolean;
  // A crossfade is waiting for the page, and nothing paints meanwhile.
  crossfading: boolean;
  release: () => void;
}

// The route change whose new page hasn't fully arrived.
let hold: Hold | undefined;
let committedKey: string | undefined;
let loadingPages = 0;

const enteringPages = new Set<HTMLElement>();
let namedPage: HTMLElement | undefined;

// Whether a page mounting now is what a crossfade is waiting for. Once that has
// stopped waiting, anything later has to fade in by itself.
export function isRouteCrossfading() {
  return hold?.crossfading === true;
}

// For a page as it mounts: one that came with a route change rises into place.
export function addEnteringPage(page: HTMLElement) {
  if (hold) {
    enteringPages.add(page);
  }
}

// For a spinner as it mounts: a page showing one hasn't arrived. A section that
// is new renders straight away with a spinner for the page inside it, whose code
// may not have loaded: only a part already on screen can wait for that.
export function addLoadingPage() {
  loadingPages += 1;
  return () => {
    loadingPages -= 1;
    queueMicrotask(advanceHold);
  };
}

// Only what's new moves, and only its outermost page. A section's shell holds a
// sidebar that stays put, so what's beside the sidebar moves instead.
function takeNewPageParts() {
  const pages = [...enteringPages].filter((page) => page.isConnected);
  enteringPages.clear();

  return pages
    .filter(
      (page) => !pages.some((other) => other !== page && other.contains(page)),
    )
    .flatMap((page): Element[] => {
      const sidebar = page.querySelector("[data-section-sidebar]");
      return sidebar?.parentElement
        ? [...sidebar.parentElement.children].filter(
            (child) => child !== sidebar,
          )
        : [page];
    });
}

// In a crossfade the new page gets its own layer, which is what lets it arrive
// after the old page has left, instead of through it.
function nameNewPage() {
  const page = takeNewPageParts().find(
    (part): part is HTMLElement =>
      part instanceof HTMLElement && part.getClientRects().length > 0,
  );
  if (page) {
    page.style.setProperty("view-transition-name", "route-page");
    namedPage = page;
  }
}

function clearNamedPage() {
  namedPage?.style.removeProperty("view-transition-name");
  namedPage = undefined;
}

// Without a crossfade the new page is on screen already, so it moves itself.
function riseNewPage() {
  const parts = takeNewPageParts();
  const root = document.documentElement;
  // A part arriving while the page around it rises moves along with it.
  if (
    parts.length === 0 ||
    root.classList.contains("route-rising") ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }

  root.classList.add("route-rising");
  const rises = parts.map((part) =>
    part.animate(
      { transform: ["translateY(8px)", "none"] },
      { duration: 340, easing: "ease" },
    ),
  );
  void Promise.allSettled(rises.map((rise) => rise.finished)).then(() =>
    root.classList.remove("route-rising"),
  );
}

function advanceHold() {
  if (!hold) {
    return;
  }

  const arrived = committedKey === hold.key && loadingPages === 0;
  if (!hold.crossfading) {
    riseNewPage();
  } else if (arrived) {
    // Before the crossfade takes its picture of the new page.
    nameNewPage();
  }
  if (arrived) {
    hold.release();
    hold = undefined;
  }
}

function changeRoute(key: string, update: () => void) {
  // A redirect, or another click before the new page has arrived. Wait for this
  // location instead of starting a second crossfade: that would cut the first
  // short and start from what has rendered so far, which for a redirect is a
  // half-empty page.
  if (hold) {
    hold.key = key;
    hold.update = update;
    if (hold.started) {
      startTransition(update);
    }
    return;
  }

  const current: Hold = {
    key,
    update,
    started: false,
    crossfading: false,
    release: () => undefined,
  };
  const released = new Promise<void>((resolve) => {
    current.release = resolve;
  });
  hold = current;

  const navigate = () => {
    current.started = true;
    startTransition(current.update);
  };
  const root = document.documentElement;

  const transition =
    "startViewTransition" in document &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? document.startViewTransition(() => {
          navigate();
          return released;
        })
      : undefined;
  if (transition) {
    current.crossfading = true;
    // A hidden tab rejects these, which is fine.
    transition.ready.catch(() => undefined);
    transition.updateCallbackDone.catch(() => undefined);
    void transition.finished.catch(() => undefined).finally(clearNamedPage);
  } else {
    navigate();
  }

  let slow = setTimeout(() => {
    if (committedKey !== current.key) {
      // Nothing has rendered, so the old page stays alive and usable, with a
      // loading bar at the top until the new one is ready.
      current.crossfading = false;
      transition?.skipTransition();
      root.classList.add("route-loading");
    } else if (current.crossfading) {
      slow = setTimeout(() => {
        // Crossfade to what's there, and the rest comes in place.
        current.crossfading = false;
        nameNewPage();
        current.release();
      }, MAX_HOLD_FOR_SPINNER_MS - MAX_HOLD_MS);
    }
  }, MAX_HOLD_MS);
  void released.then(() => {
    clearTimeout(slow);
    root.classList.remove("route-loading");
  });
}

// The history to give the Router. Everything else about it is the original's.
export function withRouteTransitions(history: History): History {
  const wrapped = Object.create(history) as History;
  wrapped.listen = (listener) => {
    let { pathname } = history.location;
    return history.listen((location, action) => {
      const samePage = location.pathname === pathname;
      pathname = location.pathname;
      if (samePage && !hold) {
        listener(location, action);
      } else {
        changeRoute(location.key ?? "", () => listener(location, action));
      }
    });
  };
  return wrapped;
}

// Render once inside the Router: tells the above when a location has committed.
export function RouteTransitionCommit() {
  const { key = "" } = useLocation();

  useLayoutEffect(() => {
    committedKey = key;
    // After the rest of this commit's layout effects: in those, a <Redirect> on
    // the new page changes location again, and the page's parts add themselves.
    queueMicrotask(advanceHold);
  }, [key]);

  return null;
}
